import rewardImg from "@/assets/icons/reward_highlight.svg";
import { EventDispatcher } from "@/events/EventDispatcher";
import StoreProxy from "@/store/StoreProxy";
import { TwitchatDataTypes } from "@/types/TwitchatDataTypes";
import Config from "@/utils/Config";
import Utils from "@/utils/Utils";
import BTTVUtils from "@/utils/emotes/BTTVUtils";
import FFZUtils from "@/utils/emotes/FFZUtils";
import SevenTVUtils from "@/utils/emotes/SevenTVUtils";
import { TwitchScopes } from "@/utils/twitch/TwitchScopes";
import TwitchUtils from "@/utils/twitch/TwitchUtils";
import * as tmi from "tmi.js";
import MessengerClientEvent from "./MessengerClientEvent";
import * as Sentry from "@sentry/vue";
import Logger from "@/utils/Logger";
import Database from "@/store/Database";
import { reactive } from "vue";

type AnyUserstate =
	| tmi.ChatUserstate
	| tmi.SubUserstate
	| tmi.SubGiftUpgradeUserstate
	| tmi.SubGiftUserstate
	| tmi.AnonSubGiftUserstate
	| tmi.AnonSubGiftUpgradeUserstate
	| tmi.PrimeUpgradeUserstate;

/**
 * Created : 25/09/2022
 */
export default class TwitchMessengerClient extends EventDispatcher {
	private static _instance: TwitchMessengerClient;
	private _client!: tmi.Client;
	private _connectTimeout: number = -1;
	private _watchdogTO: number = -1;
	private _reconnecting: boolean = false;
	private _authFailureNotified: boolean = false;
	private _lastPong: { date: number; latency: number } | null = null;
	private _connectedChans: { [key: string]: boolean } = reactive({});
	private _pendingChans: { [key: string]: boolean } = {};
	private _channelList: string[] = [];
	private _channelLoginToId: { [key: string]: string } = {};

	/********************
	 * GETTER / SETTERS *
	 ********************/
	static get instance(): TwitchMessengerClient {
		if (!TwitchMessengerClient._instance) {
			TwitchMessengerClient._instance = new TwitchMessengerClient();
		}
		return TwitchMessengerClient._instance;
	}

	/******************
	 * PUBLIC METHODS *
	 ******************/
	/**
	 * Connect to a channel
	 * @param channel
	 * @param anonymous
	 */
	public connectToChannel(channel: string): void {
		if (this._channelList.indexOf(channel) === -1) {
			this._channelList.push(channel);
		}

		//Debounce connection calls if calling it for multiple channels at once
		clearTimeout(this._connectTimeout);
		this._connectTimeout = window.setTimeout(async () => {
			Logger.instance.log("irc", {
				info: "Initial connect to channel(s) " + this._channelList.join(", "),
			});
			const chans = await TwitchUtils.getUserInfo(undefined, this._channelList);
			if (chans.length === 0) {
				Logger.instance.log("irc", {
					info:
						"Initial connect failed for channel(s) " +
						this._channelList.join(", ") +
						". Matching user not found on Twitch.",
					data: chans,
				});
				StoreProxy.common.alert("Unable to load user info: " + this._channelList);
				return;
			}

			const meObj = StoreProxy.auth.twitch.user;

			chans.forEach(async (channel) => {
				//Skip it if already connected or connecting
				if (this._connectedChans[channel.id] === true) return;
				if (this._pendingChans[channel.id] === true) return;
				this._pendingChans[channel.id] = true;

				this._channelLoginToId[channel.login] = channel.id;

				const u = StoreProxy.users.getUserFrom(
					"twitch",
					channel.id,
					channel.id,
					channel.login,
					channel.display_name,
					undefined,
					undefined,
					false,
					undefined,
					false,
				); //Preload user to storage
				u.channelInfo[channel.id]!.online = true;
				u.channelInfo[channel.id]!.is_broadcaster = true;

				//Init stream info
				if (!StoreProxy.stream.currentStreamInfo[channel.id]) {
					//Don't init if already existing. Authenticated user's stream info
					//are loaded during auth process
					StoreProxy.stream.currentStreamInfo[channel.id] = {
						title: "",
						category: "",
						live: false,
						started_at: 0,
						tags: [],
						user: u,
						viewers: 0,
						lastSoDoneDate: 0,
						previewUrl: "",
					};
				}

				void TwitchUtils.loadEmoteSets(channel.id);
				void TwitchUtils.loadUserBadges(channel.id);
				void TwitchUtils.loadCheermoteList(channel.id);
				void TwitchUtils.getRoomSettings(channel.id).then((settings) => {
					if (settings) {
						StoreProxy.stream.setRoomSettings(channel.id, settings);
						if (
							settings.chatDelay ||
							settings.emotesOnly ||
							settings.subOnly ||
							typeof settings.followOnly === "number"
						) {
							const message: TwitchatDataTypes.MessageRoomSettingsData = {
								id: Utils.getUUID(),
								date: Date.now(),
								type: "room_settings",
								platform: "twitch",
								channel_id: channel.id,
								channel_name: channel.login,
								settings,
							};
							void StoreProxy.chat.addMessage(message);
						}
					}
				});
				//Load chatters list if we have necessary rights
				void TwitchUtils.getChatters(channel.id, channel.login).then((res) => {
					(res || []).forEach((login) => {
						//Don't notify when joining our own room.
						//There's a "connect" notification for that
						if (channel.login != login || channel.id != meObj.id) {
							this.onJoin(channel.login, login, login == meObj.login, true);
						}
					});
				});
				BTTVUtils.instance.addChannel(channel.id);
				FFZUtils.instance.addChannel(channel.id);
				SevenTVUtils.instance.addChannel(channel.id);

				if (this._client) {
					Logger.instance.log("irc", { info: "Join channel " + channel.login });
					this._client.join(channel.login).catch((error) => {
						delete this._pendingChans[channel.id];
						Logger.instance.log("irc", {
							info: "Failed joining channel " + channel.login,
							data: error,
						});
					});
				}
			});

			//Not yet connected to IRC, create client with specified channels and credentials
			if (!this._client) {
				Logger.instance.log("irc", { info: "Create IRC client" });
				this._client = new tmi.Client({
					connection: { maxReconnectInterval: 2000 },
					channels: this._channelList.concat(),
					identity: {
						username: StoreProxy.auth.twitch.user.login,
						password: () => "oauth:" + StoreProxy.auth.twitch.access_token,
					},
					//Only keep errors (ping timeouts, auto reconnects, ...) to diagnose disconnections
					logger: {
						info: () => {},
						warn: () => {},
						error: (message: string) => {
							console.log("[IRC] " + message);
							Logger.instance.log("irc", { info: "TMI error: " + message });
						},
					},
				});
				void this.initialize();
			}

			//Does nothing if already connected or connecting
			this._client.connect().catch((error) => {
				Logger.instance.log("irc", { info: "Connection failed", data: error });
			});
		}, 1000);
	}

	/**
	 * Gets if IRC is connected to the given channel ID
	 * Reactive, can be used inside a computed
	 */
	public getIsConnectedToChannelID(id: string): boolean {
		return this._connectedChans[id] === true;
	}

	/**
	 * Disconnect from a specific channel
	 * @param channel
	 */
	public async disconnectFromChannel(channel: string): Promise<void> {
		Logger.instance.log("irc", { info: "Disconnect from channel " + channel });
		const index = this._channelList.findIndex((v) => v === channel);
		if (index > -1) {
			this._channelList.splice(index, 1);
			this._client.part("#" + channel).catch((error) => {
				Logger.instance.log("irc", {
					info: "Failed leaving channel " + channel,
					data: error,
				});
			});
		}
		const channelId = this._channelLoginToId[channel];
		if (channelId) delete this._pendingChans[channelId];
	}

	/**
	 * Refresh IRC token
	 * Disconnects from all chans and connects back to it
	 */
	public async refreshToken(): Promise<void> {
		if (!this._client) return;
		Logger.instance.log("irc", { info: "Refreshing token" });
		await this.reconnect();
	}

	/**
	 * Disconnect from all channels and cut IRC connection
	 */
	public disconnect(): void {
		if (this._client) {
			this._client.disconnect().catch(() => {
				/* ignore, TMI's logger already logged it */
			});
		}
	}

	/**
	 * Sends a message to a channel or executes a chat command
	 */
	public async sendMessage(
		channelId: string,
		text: string,
		replyTo?: TwitchatDataTypes.MessageChatData,
		noConfirmCommercial: boolean = false,
		sendAsBot: boolean = true,
	): Promise<boolean> {
		let pinMessage = false;
		if (text.charAt(0) == "/") {
			const chunks = text.split(/\s/gi).filter((v) => v != "");
			let cmd = (chunks.shift() as string).toLowerCase();

			//If using /announce command, extract color
			if (cmd.indexOf("/announce") === 0) {
				let color = cmd.replace("/announce", "");
				if (color.length === 0) color = "primary";
				if (["blue", "green", "orange", "purple", "primary"].indexOf(color) === -1) {
					StoreProxy.common.alert("Invalid announcement color");
					return false;
				}
				cmd = "/announce";
				chunks.unshift(color);
			}

			async function getUserFromLogin(
				login: string,
				channelId: string,
			): Promise<TwitchatDataTypes.TwitchatUser | null> {
				login = login.replace(/^@/, "");
				return new Promise((resolve) => {
					StoreProxy.users.getUserFrom(
						"twitch",
						channelId,
						undefined,
						login,
						undefined,
						(user) => {
							if (user.errored) {
								StoreProxy.common.alert(
									'User <strong>"' + login + '"</strong> does not exists',
								);
							}
							resolve(user);
						},
						undefined,
						undefined,
						undefined,
						false,
					);
				});
				// let res:TwitchDataTypes.UserInfo[];
				// try {
				// 	res = await TwitchUtils.getUserInfo(undefined, [login])
				// }catch(error) {
				// 	StoreProxy.common.alert("User @"+login+" not found on Twitch.");
				// 	return null;
				// }
				// return res[0];
			}

			switch (cmd) {
				case "/announce": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SEND_ANNOUNCE])) return false;
					await TwitchUtils.sendAnnouncement(
						channelId,
						chunks.splice(1).join(" "),
						chunks[0] as "blue" | "green" | "orange" | "purple" | "primary",
						sendAsBot,
					);
					return true;
				}
				case "/warn": {
					if (!TwitchUtils.requestScopes([TwitchScopes.CHAT_WARNING])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user)
						return await TwitchUtils.sendWarning(
							user.id,
							chunks.splice(1).join(" "),
							channelId,
						);
					return false;
				}
				case "/ban": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_BANNED])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user)
						return await TwitchUtils.banUser(
							user,
							channelId,
							undefined,
							chunks.splice(1).join(" "),
						);
					return false;
				}
				case "/unban": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_BANNED])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user) return await TwitchUtils.unbanUser(user, channelId);
					return false;
				}
				case "/block": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_BLOCKED])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user) return await TwitchUtils.blockUser(user);
					return false;
				}
				case "/unblock": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_BLOCKED])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user) return await TwitchUtils.unblockUser(user);
					return false;
				}
				case "/banword": {
					if (!TwitchUtils.requestScopes([TwitchScopes.BLOCKED_TERMS])) return false;
					const words = chunks.join(" ");
					if (words.length == 0) return false;
					return (await TwitchUtils.addBanword(words, channelId)) != false;
				}
				case "/unbanword": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_BANNED])) return false;
					const words = chunks.join(" ");
					if (words.length == 0) return false;
					const banwordList = await TwitchUtils.getBanword(channelId);
					const word = banwordList.find(
						(v) => v.text.trim().toLowerCase() == words.trim().toLowerCase(),
					);
					if (!word) {
						const message = StoreProxy.i18n.t("error.banword_not_found", {
							WORDS: words,
						});

						const event = new MessengerClientEvent("NOTICE", {
							platform: "twitch",
							type: TwitchatDataTypes.TwitchatMessageType.NOTICE,
							id: Utils.getUUID(),
							channel_id: channelId,
							date: Date.now(),
							message,
							noticeId: "error",
						});
						this.dispatchEvent(event);
						return true;
					}
					return (await TwitchUtils.removeBanword(word.id, channelId)) != false;
				}
				case "/timeout": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_BANNED])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user)
						return await TwitchUtils.banUser(
							user,
							channelId,
							parseInt(chunks[1]!) || 600,
							chunks.splice(2).join(" "),
						);
					return false;
				}
				case "/untimeout": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_BANNED])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user) return await TwitchUtils.unbanUser(user, channelId);
					return false;
				}
				case "/vip": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_VIPS])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user) return await TwitchUtils.addRemoveVIP(false, channelId, user);
					return false;
				}
				case "/unvip": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_VIPS])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user) return await TwitchUtils.addRemoveVIP(true, channelId, user);
					return false;
				}
				case "/mod": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_MODS])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user) return await TwitchUtils.addRemoveModerator(false, channelId, user);
					return false;
				}
				case "/mods": {
					if (!TwitchUtils.requestScopes([TwitchScopes.READ_MODERATORS])) return false;
					const mods = await TwitchUtils.getModerators();
					if (mods.length === 0)
						mods.push({ user_login: "none", user_id: "0", user_name: "None" });
					const m: TwitchatDataTypes.MessageNoticeData = {
						channel_id: channelId,
						date: Date.now(),
						id: Utils.getUUID(),
						message: "Moderators: " + mods.map((u) => u.user_login).join(", "),
						noticeId: TwitchatDataTypes.TwitchatNoticeType.GENERIC,
						platform: "twitch",
						type: TwitchatDataTypes.TwitchatMessageType.NOTICE,
					};
					void StoreProxy.chat.addMessage(m);
					return true;
				}
				case "/vips": {
					if (!TwitchUtils.requestScopes([TwitchScopes.READ_VIPS])) return false;
					const vips = await TwitchUtils.getVIPs();
					if (vips.length === 0)
						vips.push({ user_login: "none", user_id: "0", user_name: "None" });
					const m: TwitchatDataTypes.MessageNoticeData = {
						channel_id: channelId,
						date: Date.now(),
						id: Utils.getUUID(),
						message: "VIPs: " + vips.map((u) => u.user_login).join(", "),
						noticeId: TwitchatDataTypes.TwitchatNoticeType.GENERIC,
						platform: "twitch",
						type: TwitchatDataTypes.TwitchatMessageType.NOTICE,
					};
					void StoreProxy.chat.addMessage(m);
					return true;
				}
				case "/unmod": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_MODS])) return false;
					const user = await getUserFromLogin(chunks[0]!, channelId);
					if (user) return await TwitchUtils.addRemoveModerator(true, channelId, user);
					return false;
				}
				case "/commercial": {
					if (
						!StoreProxy.auth.twitch.user.is_affiliate &&
						!StoreProxy.auth.twitch.user.is_partner
					)
						return false;
					if (!TwitchUtils.requestScopes([TwitchScopes.START_COMMERCIAL])) return false;
					const duration = parseInt(chunks[0]!);
					void StoreProxy.stream.startCommercial(
						channelId,
						duration,
						noConfirmCommercial,
					);
					return true;
				}
				case "/shield": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SHIELD_MODE])) return false;
					return await TwitchUtils.setShieldMode(channelId, true);
				}
				case "/shieldoff": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SHIELD_MODE])) return false;
					return await TwitchUtils.setShieldMode(channelId, false);
				}
				case "/delete": {
					if (!TwitchUtils.requestScopes([TwitchScopes.DELETE_MESSAGES])) return false;
					return await TwitchUtils.deleteMessages(channelId, chunks[0]!);
				}
				case "/clear": {
					if (!TwitchUtils.requestScopes([TwitchScopes.DELETE_MESSAGES])) return false;
					return await TwitchUtils.deleteMessages(channelId, undefined, true);
				}
				case "/color": {
					if (!TwitchUtils.requestScopes([TwitchScopes.EDIT_BANNED])) return false;
					return await TwitchUtils.setColor(chunks[0]!);
				}
				case "/emoteonly": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SET_ROOM_SETTINGS])) return false;
					return await TwitchUtils.setRoomSettings(channelId, { emotesOnly: true });
				}
				case "/emoteonlyoff": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SET_ROOM_SETTINGS])) return false;
					return await TwitchUtils.setRoomSettings(channelId, { emotesOnly: false });
				}
				case "/followers": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SET_ROOM_SETTINGS])) return false;
					return await TwitchUtils.setRoomSettings(channelId, {
						followOnly: parseInt(chunks[0]!),
					});
				}
				case "/followersoff": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SET_ROOM_SETTINGS])) return false;
					return await TwitchUtils.setRoomSettings(channelId, { followOnly: false });
				}
				case "/slow": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SET_ROOM_SETTINGS])) return false;
					return await TwitchUtils.setRoomSettings(channelId, {
						slowMode: parseInt(chunks[0]!),
					});
				}
				case "/slowoff": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SET_ROOM_SETTINGS])) return false;
					return await TwitchUtils.setRoomSettings(channelId, { slowMode: 0 });
				}
				case "/subscribers": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SET_ROOM_SETTINGS])) return false;
					return await TwitchUtils.setRoomSettings(channelId, { subOnly: true });
				}
				case "/subscribersoff": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SET_ROOM_SETTINGS])) return false;
					return await TwitchUtils.setRoomSettings(channelId, { subOnly: false });
				}
				case "/raid": {
					if (!TwitchUtils.requestScopes([TwitchScopes.START_RAID])) return false;
					//First check for channel.moderate v1 scope
					if (
						!TwitchUtils.hasScopes([TwitchScopes.MODERATION_EVENTS]) &&
						//Alternatively check for all permissions required by channel.moderate v2
						!TwitchUtils.hasScopes([
							TwitchScopes.START_RAID,
							TwitchScopes.BLOCKED_TERMS,
							TwitchScopes.SET_ROOM_SETTINGS,
							TwitchScopes.UNBAN_REQUESTS,
							TwitchScopes.EDIT_BANNED,
							TwitchScopes.DELETE_MESSAGES,
							TwitchScopes.CHAT_WARNING,
							TwitchScopes.READ_MODERATORS,
							TwitchScopes.READ_VIPS,
						])
					)
						return false;
					return await TwitchUtils.raidChannel(
						chunks[0]!.replace(/^@/, "").toLowerCase(),
					);
				}
				case "/unraid": {
					if (!TwitchUtils.requestScopes([TwitchScopes.START_RAID])) return false;
					return await TwitchUtils.raidCancel();
				}
				case "/clip": {
					let title = "";
					let duration = parseInt(chunks[0]!);
					if (isNaN(duration)) {
						if (chunks.length > 0) {
							title = chunks.join(" ");
						}
					} else {
						title = chunks.splice(1).join(" ");
					}
					if (!TwitchUtils.requestScopes([TwitchScopes.CLIPS_EDIT])) return false;
					return await TwitchUtils.createClip(duration, title);
				}
				case "/whisper":
				case "/w": {
					if (!TwitchUtils.requestScopes([TwitchScopes.WHISPER_MANAGE])) return false;
					const login = chunks[0]!.replace(/^@/, "").toLowerCase();
					return await TwitchUtils.whisper(chunks.splice(1).join(" "), login);
				}
				case "/marker": {
					if (!TwitchUtils.requestScopes([TwitchScopes.SET_STREAM_INFOS])) return false;
					const comment = chunks[0];
					return await TwitchUtils.createStreamMarker(comment);
				}
				case "/pin": {
					pinMessage = true;
					text = text.replace(/^\/pin ?/, "");
					break;
				}
				case "/unpin": {
					const message =
						StoreProxy.chat.pinnedTwitchMessage[StoreProxy.auth.twitch.user.id];
					if (!message) return true;
					return await TwitchUtils.unpinMessage(
						message.broadcaster_id,
						message.message_id,
					);
				}

				//TODO
				case "/uniquechat":
					return false;
				case "/uniquechatoff":
					return false;
			}
		}
		if (replyTo) {
			await TwitchUtils.sendMessage(channelId, text, replyTo, sendAsBot);
		} else {
			if (pinMessage) {
				if (!TwitchUtils.requestScopes([TwitchScopes.DELETE_MESSAGES])) {
					return false;
				}
			}
			await TwitchUtils.sendMessage(channelId, text, undefined, sendAsBot, pinMessage);
		}
		return true;
	}

	/*******************
	 * PRIVATE METHODS *
	 *******************/
	private async initialize(): Promise<void> {
		this._client.on("message", this.onMessage.bind(this));
		this._client.on("join", this.onJoin.bind(this));
		this._client.on("part", this.onLeave.bind(this));
		this._client.on("cheer", this.onCheer.bind(this));
		this._client.on("resub", this.resub.bind(this));
		this._client.on("subscription", this.subscription.bind(this));
		this._client.on("primepaidupgrade", this.subscriptionPrimeUpgrade.bind(this));
		this._client.on("subgift", this.subgift.bind(this));
		this._client.on("anonsubgift", this.anonsubgift.bind(this));
		this._client.on("giftpaidupgrade", this.giftpaidupgrade.bind(this));
		this._client.on("anongiftpaidupgrade", this.anongiftpaidupgrade.bind(this));
		this._client.on("ban", this.onBanUser.bind(this));
		this._client.on("timeout", this.onTimeoutUser.bind(this));
		this._client.on("disconnected", this.disconnected.bind(this));
		this._client.on("messagedeleted", this.onDeleteMessage.bind(this));
		this._client.on("raw_message", this.raw_message.bind(this));
		this._client.on("pong", (latency: number) => {
			this._lastPong = { date: Date.now(), latency };
			//TMI cuts the connection if PONG takes more than ~10s, log slow ones
			if (latency >= 2) {
				Logger.instance.log("irc", { info: "Slow PONG received after " + latency + "s" });
			}
		});
		//Log socket close codes to diagnose disconnections
		this._client.on("socketclose", (event) => {
			Logger.instance.log("irc", {
				info: "Socket closed with code " + event.code,
				data: {
					reason: event.reason,
					wasClean: event.wasClean,
					lastPong: this._lastPong && {
						latency: this._lastPong.latency,
						msAgo: Date.now() - this._lastPong.date,
					},
				},
			});
		});

		// Workaround a potential TMI issue not detecting if socket died when computer/page
		// is asleep.
		const schedule = () => {
			if (document.hidden || !navigator.onLine) return;
			clearTimeout(this._watchdogTO);
			//Leave TMI a chance to notice and recover by itself first
			this._watchdogTO = window.setTimeout(() => void this.checkConnection(), 5000);
		};
		document.addEventListener("visibilitychange", schedule);
		window.addEventListener("online", schedule);

		const hashmap: { [key: string]: boolean } = {};
		try {
			//Load bots list
			const res = await fetch("https://api.twitchinsights.net/v1/bots/all");
			const json = await res.json();
			(json.bots as string[][]).forEach((b) => (hashmap[b[0]!.toLowerCase()] = true));
		} catch (_error) {
			//Fallback in case twitchinsights dies someday
			[
				"streamelements",
				"nightbot",
				"wizebot",
				"kikettebot",
				"commanderroot",
				"anotherttvviewer",
				"streamlabs",
				"communityshowcase",
			].forEach((b) => (hashmap[b[0]!.toLowerCase()] = true));
			Logger.instance.log("irc", { info: "Failed loading bots list, using a static one" });
		}
		StoreProxy.users.setBotsMap("twitch", hashmap);
	}

	/**
	 * Refresh IRC connection
	 * Called after updating the token or when the socket died
	 */
	private async reconnect(): Promise<void> {
		if (this._reconnecting) return;
		this._reconnecting = true;
		Logger.instance.log("irc", { info: "Force IRC reconnect" });

		try {
			await this._client.forceReconnect();
		} catch (error) {
			Logger.instance.log("irc", { info: "Reconnection failed", data: error });
		} finally {
			this._reconnecting = false;
		}
	}

	/**
	 * Check if TMI socket died and reconnects if it did
	 */
	private async checkConnection(): Promise<void> {
		if (!this._client) return;
		if (document.hidden || !navigator.onLine) return;

		const state = this._client.readyState();

		if (state === "OPEN") {
			// Forces TMI to ping. If no pong is received TMI will cut the connection
			void this._client.ping().catch(() => {
				/* ignore */
			});
			return;
		}

		if (state === "CONNECTING") return;

		Logger.instance.log("irc", {
			info: 'Connection found "' + state + '" on wake up, reconnecting',
		});
		await this.reconnect();
	}

	/**
	 * Gets a user object from IRC tags
	 * @param tags
	 * @returns
	 */
	private getUserFromTags(tags: AnyUserstate, channelId: string): TwitchatDataTypes.TwitchatUser {
		const login = tags.login ?? tags.username ?? tags["display-name"];
		const isMod = tags.badges?.moderator != undefined || tags.mod === true;
		const isVip = tags.badges?.vip != undefined || tags.vip === true;
		const isSub = tags.badges?.subscriber != undefined || tags.subscriber === true;
		const isSubGifter = tags.badges && tags.badges["sub-gifter"] != undefined;
		const isBroadcaster = tags.badges?.broadcaster != undefined;
		const isPartner = tags.badges?.partner != undefined;
		const user = StoreProxy.users.getUserFrom(
			"twitch",
			channelId,
			tags["user-id"],
			login,
			tags["display-name"],
			undefined,
			false,
			true,
			isSub,
		);
		const channelInfo = user.channelInfo[channelId]!;

		// user.channelInfo[channelId].online	= true;
		delete user.temporary; //Avoids useless server call

		if (tags.color) user.color = tags.color;

		//Chat messages are the only source telling for sure what roles a user has on this
		//channel. Other events miss the "vip" tag or aren't bound to a channel, and messages
		//from a shared chat session may give the roles on the source channel.
		const isChatMessage = tags["message-type"] == "chat" || tags["message-type"] == "action";
		const isOtherChannel = tags["source-room-id"] && tags["source-room-id"] != tags["room-id"];
		const canRemoveRoles = isChatMessage && !isOtherChannel;

		if (isMod && !channelInfo.is_moderator) {
			StoreProxy.users.flagMod("twitch", channelId, user.id);
		} else if (!isMod && channelInfo.is_moderator && canRemoveRoles && user.id != channelId) {
			StoreProxy.users.flagUnmod("twitch", channelId, user.id);
		}
		if (isVip && !channelInfo.is_vip) {
			StoreProxy.users.flagVip("twitch", channelId, user.id);
		} else if (!isVip && channelInfo.is_vip && canRemoveRoles) {
			StoreProxy.users.flagUnvip("twitch", channelId, user.id);
		}
		if (isSub) channelInfo.is_subscriber = true;
		if (isSubGifter) channelInfo.is_gifter = true;
		if (isBroadcaster) channelInfo.is_broadcaster = true;
		if (isPartner) {
			user.is_partner = true;
			user.is_affiliate = true;
		}

		if (tags.badges && tags["room-id"]) {
			channelInfo.badges = TwitchUtils.getBadgesFromRawBadges(
				tags["room-id"],
				tags["badge-info"],
				tags.badges,
			);
		} else {
			//Cleanup badges from the user
			channelInfo.badges = [];
		}
		return user;
	}

	private getChannelID(login: string): string {
		return this._channelLoginToId[login.replace("#", "")]!;
	}

	/**
	 * Gets a user's state object from its login
	 *
	 * @param login
	 * @param channelId
	 * @returns
	 */
	private getUserStateFromLogin(
		login: string,
		channelId: string,
	): { user: TwitchatDataTypes.TwitchatUser; wasOnline: boolean } {
		//Search if a user with this name and source exists on store
		//If no user exists a temporary user object will be returned and
		//populated asynchronously via an API call
		const user = StoreProxy.users.getUserFrom("twitch", channelId, undefined, login);
		const wasOnline = user.channelInfo[channelId]!.online === true;
		// user.channelInfo[channelId].online = true;
		return { user, wasOnline };
	}

	/**
	 * Gets a sub object from data
	 *
	 * @param channel
	 * @param tags
	 * @param methods
	 * @param message
	 * @returns
	 */
	private getCommonSubObject(
		channel: string,
		tags: AnyUserstate,
		methods?: tmi.SubMethods,
		message?: string,
	): TwitchatDataTypes.MessageSubscriptionData {
		const channel_id = this.getChannelID(channel);
		let prepaidMonths = this.getNumValueFromTag(tags["msg-param-multimonth-duration"], 1);
		let isNewMultimonth = tags["msg-param-multimonth-tenure"] === false;
		if (!isNewMultimonth || (prepaidMonths != 3 && prepaidMonths != 6)) prepaidMonths = 1;
		const res: TwitchatDataTypes.MessageSubscriptionData = {
			platform: "twitch",
			type: TwitchatDataTypes.TwitchatMessageType.SUBSCRIPTION,
			id: tags.id ?? Utils.getUUID(),
			channel_id,
			date: parseInt((tags["tmi-sent-ts"] as string) ?? Date.now().toString()),
			user: this.getUserFromTags(tags, channel_id),
			tier: 1,
			is_gift: false,
			is_giftUpgrade: false,
			is_resub: false,
			is_primeUpgrade: false,
			is_targetedSubgift: tags["msg-param-community-gift-id"] == undefined,
			months: prepaidMonths,
			streakMonths: this.getNumValueFromTag(tags["msg-param-streak-months"], 1),
			totalSubDuration: this.getNumValueFromTag(tags["msg-param-cumulative-months"], 1),
			raw_data: { tags, methods, message },
			message_size: 0,
		};
		if (methods)
			res.tier = methods.prime
				? "prime"
				: ((parseInt((methods.plan as string) || "1000") / 1000) as 1 | 2 | 3);
		if (message) {
			const chunks = TwitchUtils.parseMessageToChunks(message, tags["emotes-raw"]);
			res.message = message;
			res.message_chunks = chunks;
			res.message_html = TwitchUtils.messageChunksToHTML(chunks);
			res.message_size = TwitchUtils.computeMessageSize(chunks);
		}
		return res;
	}

	/**
	 * Dispatches a sub related event unless it comes from a shared chat session
	 * of a channel we're already connected to
	 */
	private dispatchSubEvent(
		data: TwitchatDataTypes.MessageSubscriptionData,
		tags: AnyUserstate,
	): void {
		//Ignore event if it comes from a shared chat session we're already connected to
		if (!TwitchUtils.flagSharedChatMessage(data, tags["source-room-id"], tags["room-id"]))
			return;

		this.dispatchEvent(new MessengerClientEvent("SUB", data));
	}

	private onMessage(
		channel: string,
		tags: tmi.ChatUserstate,
		message: string,
		_self: boolean,
	): void {
		//Ignore anything that's not a message or a /me
		if (
			tags["message-type"] != "chat" &&
			tags["message-type"] != "whisper" &&
			tags["message-type"] != "action" &&
			tags["message-type"] != "announcement"
		) {
			if (!Config.instance.IS_PROD) {
				let data: TwitchatDataTypes.MessageNoticeData = {
					channel_id: this.getChannelID(channel),
					date: Date.now(),
					id: Utils.getUUID(),
					message: "Unknown message type: " + tags["message-type"],
					platform: "twitch",
					type: TwitchatDataTypes.TwitchatMessageType.NOTICE,
					noticeId: TwitchatDataTypes.TwitchatNoticeType.GENERIC,
				};
				this.dispatchEvent(new MessengerClientEvent("NOTICE", data));
			}
			return;
		}

		//Ignore rewards with text, they are also sent to EventSub
		if (tags["custom-reward-id"]) return;

		//Receive whispers
		if (tags["message-type"] == "whisper") {
			const channel_id = StoreProxy.auth.twitch.user.id;
			const user = this.getUserFromTags(tags, channel_id);

			const data: TwitchatDataTypes.MessageWhisperData = {
				id: tags.id ? tags.id : channel_id + tags["message-id"],
				type: TwitchatDataTypes.TwitchatMessageType.WHISPER,
				to: StoreProxy.auth.twitch.user,
				platform: "twitch",
				channel_id,
				date: Date.now(),
				user,
				message,
				message_html: "",
				message_chunks: [],
				message_size: 0,
			};

			data.message_chunks = TwitchUtils.parseMessageToChunks(message, tags["emotes-raw"]);
			data.message_html = TwitchUtils.messageChunksToHTML(data.message_chunks);
			data.message_size = TwitchUtils.computeMessageSize(data.message_chunks);
			this.dispatchEvent(new MessengerClientEvent("WHISPER", data));
			return;
		}

		const channel_id = this.getChannelID(channel);
		const user = this.getUserFromTags(tags, channel_id);

		const data: TwitchatDataTypes.MessageChatData = {
			id: tags.id!,
			type: TwitchatDataTypes.TwitchatMessageType.MESSAGE,
			platform: "twitch",
			channel_id,
			date: Date.now(),
			user,
			message,
			answers: [],
			message_html: "",
			message_chunks: [],
			message_size: 0,
			is_short: false,
			raw_data: { tags, message },
			twitch_source: "irc",
		};

		//Ignore message if it comes from a shared chat session we're already connected to
		if (!TwitchUtils.flagSharedChatMessage(data, tags["source-room-id"], tags["room-id"]))
			return;

		data.message_chunks = TwitchUtils.parseMessageToChunks(message, tags["emotes-raw"]);
		data.message_html = TwitchUtils.messageChunksToHTML(data.message_chunks);
		data.message_size = TwitchUtils.computeMessageSize(data.message_chunks);
		data.is_short =
			Utils.stripHTMLTags(data.message_html).length / data.message.length < 0.6 ||
			data.message.length < 4;

		// If message is an answer, set original message's ref to the answer
		// Called when using the "answer feature" on twitch chat
		if (tags["reply-parent-msg-id"]) {
			const messages = StoreProxy.chat.messages;
			//Search for original message the user answered to
			for (let i = messages.length - 1; i >= Math.max(0, messages.length - 1000); i--) {
				let m = messages[i]!;
				if (m.type != TwitchatDataTypes.TwitchatMessageType.MESSAGE) continue;
				if (m.id === tags["reply-parent-msg-id"]) {
					if (!m.answers) m.answers = [];
					m.answers.push(data);
					StoreProxy.chat.notifyManyReplies(m);
					data.answersTo = m.answersTo || m;
					data.directlyAnswersTo = m;
					Database.instance.updateMessage(m);
					break;
				}
			}
		}

		data.twitch_isSlashMe = tags["message-type"] === "action";
		data.twitch_isReturning = tags["returning-chatter"] === true;
		data.twitch_isFirstMessage = tags["first-msg"] === true && tags["msg-id"] != "user-intro";
		data.twitch_isPresentation = tags["msg-id"] == "user-intro";
		data.twitch_isHighlighted = tags["msg-id"] === "highlighted-message";
		data.is_short = tags["emote-only"] === true;
		if (tags["msg-id"] === "gigantified-emote-message") {
			const emote = data.message_chunks.findLast((v) => v.type == "emote");
			data.twitch_gigantifiedEmote = emote?.value;
			data.twitch_gigantifiedEmote_url = emote?.emoteHD;
		}
		if (tags["msg-id"] === "animated-message") data.twitch_animationId = tags["animation-id"];
		if (tags["msg-param-color"])
			data.twitch_announcementColor = tags["msg-param-color"].toLowerCase();
		if (tags["gifs"]) {
			const gifChunks: string[] = tags["gifs"].split("|");
			const url = gifChunks[2];
			if (url && /^https?:\/\//.test(url)) {
				const range = (gifChunks[0] || "").split("-").map((v) => parseInt(v));
				const begin = range[0];
				const end = range[1];
				const description =
					begin != undefined && end != undefined && !isNaN(begin) && !isNaN(end)
						? Array.from(data.message)
								.slice(begin, end + 1)
								.join("")
						: data.message;
				data.twitch_gif = {
					url,
					description: description.replace(/^\[|\]$/g, ""),
				};
				// clear message content as it's just the GIF description
				data.message = "";
				data.message_chunks = [];
				data.message_html = "";
				data.message_size = 0;
			}
		}

		//Send reward redeem message if the message comes from an "highlight my message" reward
		if (data.twitch_isHighlighted) {
			const reward: TwitchatDataTypes.MessageRewardRedeemData = {
				channel_id: data.channel_id,
				date: data.date,
				id: Utils.getUUID(),
				platform: "twitch",
				type: TwitchatDataTypes.TwitchatMessageType.REWARD,
				user: data.user,
				reward: {
					id: Config.instance.highlightMyMessageReward.id,
					title: Config.instance.highlightMyMessageReward.title,
					cost: -1,
					description: "",
					color: Config.instance.highlightMyMessageReward.background_color,
					icon: {
						sd: rewardImg,
					},
				},
				message: data.message,
				message_chunks: data.message_chunks,
				message_html: data.message_html,
				message_size: data.message_size,
				twitchSharedChat: data.twitchSharedChat,
				twitchSharedChatSourceId: data.twitchSharedChatSourceId,
			};
			this.dispatchEvent(new MessengerClientEvent("REWARD", reward));
		}

		this.dispatchEvent(new MessengerClientEvent("MESSAGE", data));
	}

	private onJoin(
		channelName: string,
		username: string,
		self: boolean,
		isFakeJoin: boolean = false,
	): void {
		if (self && !isFakeJoin) {
			delete this._pendingChans[this.getChannelID(channelName)];
			this._authFailureNotified = false;
		}

		const channel_id = this.getChannelID(channelName);
		const userState = this.getUserStateFromLogin(username, channel_id);

		if (self) {
			//Avoid sending a connect message on chat when joining others channels
			if (isFakeJoin) return;
			const d: TwitchatDataTypes.MessageConnectData = {
				platform: "twitch",
				type: TwitchatDataTypes.TwitchatMessageType.CONNECT,
				id: Utils.getUUID(),
				date: Date.now(),
				channel_id,
				user: userState.user,
			};
			Logger.instance.log("irc", {
				info: "Connected to channel " + channelName + " #" + channel_id,
			});
			StoreProxy.users.flagOnlineUsers([userState.user], channel_id);
			this.dispatchEvent(new MessengerClientEvent("CONNECTED", d));
			this._connectedChans[channel_id] = true;
		} else {
			if (userState.wasOnline === true) return; //User was already here, don't send join notification
			this.dispatchEvent(
				new MessengerClientEvent("JOIN", {
					platform: "twitch",
					type: TwitchatDataTypes.TwitchatMessageType.JOIN,
					id: Utils.getUUID(),
					channel_id,
					date: Date.now(),
					users: [userState.user],
				}),
			);
		}
	}

	private onLeave(channelName: string, username: string): void {
		const channel_id = this.getChannelID(channelName);
		const userState = this.getUserStateFromLogin(username, channel_id);
		const me = StoreProxy.auth.twitch.user;

		this.dispatchEvent(
			new MessengerClientEvent("LEAVE", {
				platform: "twitch",
				type: TwitchatDataTypes.TwitchatMessageType.LEAVE,
				id: Utils.getUUID(),
				channel_id,
				date: Date.now(),
				users: [userState.user],
			}),
		);

		if (this._connectedChans[channel_id] === true && username == me.login) {
			this._connectedChans[channel_id] = false;

			const eventData: TwitchatDataTypes.MessageDisconnectData = {
				channel_id,
				platform: "twitch",
				id: Utils.getUUID(),
				type: TwitchatDataTypes.TwitchatMessageType.DISCONNECT,
				date: Date.now(),
				user: me,
			};
			Logger.instance.log("irc", {
				info:
					'Disconnected user "' +
					username +
					'" from channel ' +
					channelName +
					" #" +
					channel_id,
			});
			this.dispatchEvent(new MessengerClientEvent("DISCONNECTED", eventData));
		}
	}

	private async onCheer(
		channel: string,
		tags: tmi.ChatUserstate,
		message: string,
	): Promise<void> {
		const channel_id = this.getChannelID(channel);
		const chunks = TwitchUtils.parseMessageToChunks(message, tags["emotes-raw"]);
		const missingCheerTimeout = window.setTimeout(() => {
			if (StoreProxy.chat.messages.findIndex((v) => v.id == tags.id) === -1) {
				let data: TwitchatDataTypes.MessageCustomData = {
					channel_id: this.getChannelID(channel),
					date: Date.now(),
					id: Utils.getUUID(),
					message: "Cheer not found on history after 5s !",
					platform: "twitch",
					type: TwitchatDataTypes.TwitchatMessageType.CUSTOM,
					style: "error",
				};
				this.dispatchEvent(new MessengerClientEvent("MESSAGE", data));
				console.log("MISSING CHEEEEEEEEER", channel, tags, message);
			}
		}, 5000);
		await TwitchUtils.parseCheermotes(chunks, channel_id);

		const data: TwitchatDataTypes.MessageCheerData = {
			platform: "twitch",
			type: TwitchatDataTypes.TwitchatMessageType.CHEER,
			id: tags.id ?? Utils.getUUID(),
			channel_id,
			date: parseInt((tags["tmi-sent-ts"] as string) ?? Date.now().toString()),
			user: this.getUserFromTags(tags, channel_id),
			bits: this.getNumValueFromTag(tags.bits || "0", 0),
			message,
			message_chunks: chunks,
			message_html: TwitchUtils.messageChunksToHTML(chunks),
			message_size: TwitchUtils.computeMessageSize(chunks),
			raw_data: tags,
			pinned: false,
			pinLevel: 0,
			pinDuration_ms: 0,
		};

		//Ignore message if it comes from a shared chat session we're already connected to.
		//Also cancel the watchdog, the cheer is expected to be missing from the history as
		//it will be received via that channel's own connection, under a different ID
		if (!TwitchUtils.flagSharedChatMessage(data, tags["source-room-id"], tags["room-id"])) {
			clearTimeout(missingCheerTimeout);
			return;
		}

		this.dispatchEvent(new MessengerClientEvent("CHEER", data));
	}

	private resub(
		channel: string,
		username: string,
		months: number,
		message: string,
		tags: AnyUserstate,
		methods: tmi.SubMethods,
	): void {
		const data = this.getCommonSubObject(channel, tags, methods, message);
		data.is_resub = true;
		this.dispatchSubEvent(data, tags);
	}

	private subscription(
		channel: string,
		username: string,
		methods: tmi.SubMethods,
		message: string,
		tags: AnyUserstate,
	): void {
		const data = this.getCommonSubObject(channel, tags, methods, message);
		this.dispatchSubEvent(data, tags);
	}

	private subscriptionPrimeUpgrade(
		channel: string,
		username: string,
		methods: tmi.SubMethods,
		tags: AnyUserstate,
	): void {
		const data = this.getCommonSubObject(channel, tags, methods);
		data.is_primeUpgrade = true;
		this.dispatchSubEvent(data, tags);
	}

	private subgift(
		channel: string,
		username: string,
		streakMonths: number,
		recipient: string,
		methods: tmi.SubMethods,
		tags: AnyUserstate,
	): void {
		const data = this.getCommonSubObject(channel, tags, methods);
		data.is_gift = true;
		data.streakMonths = streakMonths;
		data.months = this.getNumValueFromTag(tags["msg-param-gift-months"], 1);
		const recipientLogin = tags["msg-param-recipient-user-name"] ?? recipient;
		const recipientName = tags["msg-param-recipient-display-name"] ?? recipient;
		const recipientId = tags["msg-param-recipient-id"];
		const user = StoreProxy.users.getUserFrom(
			"twitch",
			data.channel_id,
			recipientId,
			recipientLogin,
			recipientName,
			undefined,
			undefined,
			false,
			undefined,
			false,
		);
		data.gift_recipients = [user];
		data.gift_count = 1;
		this.dispatchSubEvent(data, tags);
	}

	private anonsubgift(
		channel: string,
		streakMonths: number,
		recipient: string,
		methods: tmi.SubMethods,
		tags: AnyUserstate,
	): void {
		const data = this.getCommonSubObject(channel, tags, methods);
		data.is_gift = true;
		data.streakMonths = streakMonths;
		data.months = this.getNumValueFromTag(tags["msg-param-gift-months"], 1);
		const recipientLogin = tags["msg-param-recipient-user-name"] ?? recipient;
		const recipientName = tags["msg-param-recipient-display-name"] ?? recipient;
		const recipientId = tags["msg-param-recipient-id"];
		const user = StoreProxy.users.getUserFrom(
			"twitch",
			data.channel_id,
			recipientId,
			recipientLogin,
			recipientName,
			undefined,
			undefined,
			false,
			undefined,
			false,
		);
		data.gift_recipients = [user];
		data.gift_count = 1;
		this.dispatchSubEvent(data, tags);
	}

	private giftpaidupgrade(
		channel: string,
		username: string,
		sender: string,
		tags: AnyUserstate,
	): void {
		const data = this.getCommonSubObject(channel, tags);
		data.is_giftUpgrade = true;
		data.gift_upgradeSender = this.getUserStateFromLogin(sender, data.channel_id).user;
		this.dispatchSubEvent(data, tags);
	}

	private anongiftpaidupgrade(channel: string, username: string, tags: AnyUserstate): void {
		const data = this.getCommonSubObject(channel, tags);
		data.is_giftUpgrade = true;
		this.dispatchSubEvent(data, tags);
	}

	/**
	 * Called when receivied a share chat USERNOTICE.
	 * Twitch sends "sharedchatnotice" as "msg-id" for these with a "source-msg-id"
	 * containing the actual type of notice.
	 * "source-msg-id" => "msg-id" is done before calling this function so we can
	 * rely on "msg-id"
	 * This function makes sure we receive sub events from shared chat sessions.
	 */
	private onSharedChatNotice(channel: string, tags: tmi.ChatUserstate, message: string): void {
		//Mimics TMI's own USERNOTICE parsing
		const username = (tags["display-name"] || tags.login) as string;
		const plan = (tags["msg-param-sub-plan"] as tmi.SubMethod) ?? "";
		const methods: tmi.SubMethods = {
			prime: plan.includes("Prime"),
			plan,
			planName: (tags["msg-param-sub-plan-name"] as string) || undefined,
		};
		const streakMonths = this.getNumValueFromTag(tags["msg-param-streak-months"], 0);
		const recipient = (tags["msg-param-recipient-display-name"] ||
			tags["msg-param-recipient-user-name"]) as string;

		switch (tags["msg-id"]) {
			case "sub":
				this.subscription(channel, username, methods, message, tags);
				break;
			case "resub":
				this.resub(channel, username, streakMonths, message, tags, methods);
				break;
			case "subgift":
				this.subgift(channel, username, streakMonths, recipient, methods, tags);
				break;
			case "anonsubgift":
				this.anonsubgift(channel, streakMonths, recipient, methods, tags);
				break;
			case "primepaidupgrade":
				this.subscriptionPrimeUpgrade(channel, username, methods, tags);
				break;
			case "giftpaidupgrade":
				this.giftpaidupgrade(
					channel,
					username,
					(tags["msg-param-sender-name"] || tags["msg-param-sender-login"]) as string,
					tags,
				);
				break;
			case "anongiftpaidupgrade":
				this.anongiftpaidupgrade(channel, username, tags);
				break;
		}
	}

	private disconnected(reason: string): void {
		const channel_id = StoreProxy.auth.twitch.user.id;

		//Avoid spamming "disconnected from chat" messages
		if (!this._connectedChans[channel_id]) return;

		this._connectedChans[channel_id] = false;

		console.log("Disconnected for reason: ", reason);
		Logger.instance.log("irc", { info: 'Disconnected with reason "' + reason + '"' });

		const eventData: TwitchatDataTypes.MessageDisconnectData = {
			channel_id,
			platform: "twitch",
			id: Utils.getUUID(),
			type: TwitchatDataTypes.TwitchatMessageType.DISCONNECT,
			date: Date.now(),
			user: StoreProxy.auth.twitch.user,
		};
		this.dispatchEvent(new MessengerClientEvent("DISCONNECTED", eventData));
	}

	private onDeleteMessage(
		channel: string,
		username: string,
		deletedMessage: string,
		tags: tmi.DeleteUserstate,
	): void {
		const msgID = tags["target-msg-id"];
		if (!msgID) return;
		this.dispatchEvent(new MessengerClientEvent("DELETE_MESSAGE", msgID));
	}
	// ban(channel, username, reason) {
	// timeout(channel, username, seconds, reason) {
	private onTimeoutUser(
		channel: string,
		username: string,
		reason: string,
		duration: number,
		userstate: tmi.TimeoutUserstate,
	): void {
		this.onBanUser(channel, username, reason, userstate, duration);
	}

	private onBanUser(
		channel: string,
		username: string,
		reason: string,
		userstate: tmi.BanUserstate,
		duration?: number,
	): void {
		//Wait 900ms before doing anything.
		//This is a work around an Eventsub limitation.
		//Eventsub does not allow to be notified for banned users of another channel
		//as a moderator. The only way to be notified is via IRC, right here.
		//Eventsub has more details so I kept it as the main source for this info,
		//but I use this IRC event as a fallback.
		//We wait 900ms to give it time to Eventsub to receive the event.
		//If Eventsub sent us the info the user will already be marked as banned, we
		//can then ignore the event. Otherwise, we send the notificaiton.
		//We don't wait 1s or more, otherwise if TO for 1s the user would be unbanned
		//before the setTimeout completes
		window.setTimeout(async () => {
			const channel_id = this.getChannelID(channel);
			const user = StoreProxy.users.getUserFrom(
				"twitch",
				channel_id,
				userstate["target-user-id"],
				username,
			);
			const isTO = !isNaN(duration as number);
			const chanInfo = user.channelInfo[channel_id]!;
			if (
				!chanInfo.is_banned ||
				//if user is TOed and it's a perma ban
				(chanInfo.banEndDate && !isTO) ||
				//if user is perma banned and it's a TO
				(!chanInfo.banEndDate && isTO)
			) {
				//Flag as banned. This also populates the ban reason of the user
				await StoreProxy.users.flagBanned(
					"twitch",
					channel_id,
					user.id,
					isTO ? (duration as number) : undefined,
				);
			}
		}, 990);
	}

	private async raw_message(
		raw: { [property: string]: unknown },
		parsed: { [property: string]: unknown },
	): Promise<void> {
		//TMI parses the "badges" and "badge-info" props right AFTER dispatching
		//the "raw_message" event.
		//Let's wait a frame so the props are parsed
		await Utils.promisedTimeout(0);
		const tags = parsed.tags as tmi.ChatUserstate;
		const category = tags["msg-param-category"] as string;
		switch (parsed.command) {
			case "USERNOTICE": {
				//Ignore notices coming from a shared chat session we're already connected to,
				//they'll be received via that channel's own connection
				if (
					TwitchUtils.isSharedChatDuplicate(
						tags["source-room-id"],
						tags["room-id"] as string,
					)
				)
					break;

				// Twitch sends "sharedchatnotice" for all USERNOTICE coming from another
				// chan during a shared chat session.
				if (tags["msg-id"] === "sharedchatnotice" && tags["source-msg-id"]) {
					tags["msg-id"] = tags["source-msg-id"];
					//TMI stamps this tag from the "msg-id" before dispatching, so it still
					//holds "sharedchatnotice" at this point
					tags["message-type"] = tags["msg-id"];
					const params = parsed.params as string[];
					this.onSharedChatNotice(params[0]!, tags, params[1] || "");
				}

				//Handle announcement messages
				if (tags["msg-id"] === "announcement") {
					const params = parsed.params as string[];
					tags.username = tags.login;
					this.onMessage(params[0]!, tags, params[1]!, false);
				} else //Handle viewer milestone (AKA consecutive watched streams)
				if (category === "watch-streak" || category === "watch-fk") {
					const channelId = tags["room-id"] as string;
					const user = this.getUserFromTags(tags, channelId);

					const params = parsed.params as string[];
					const message = params[1] || "";
					const message_chunks = TwitchUtils.parseMessageToChunks(
						message,
						tags["emotes-raw"],
					);
					const message_html = TwitchUtils.messageChunksToHTML(message_chunks);
					const message_size = TwitchUtils.computeMessageSize(message_chunks);

					//Add watch streak specific message
					const eventData: TwitchatDataTypes.MessageWatchStreakData = {
						channel_id: channelId,
						id: Utils.getUUID(),
						type: TwitchatDataTypes.TwitchatMessageType.USER_WATCH_STREAK,
						date: Date.now(),
						platform: "twitch",
						streak: tags["msg-param-value"] as number,
						channelPointsEarned: tags["msg-param-copoReward"] as number,
						user,
						message_chunks,
						message_html,
						message_size,
					};
					TwitchUtils.flagSharedChatMessage(
						eventData,
						tags["source-room-id"],
						tags["room-id"] as string,
					);
					this.dispatchEvent(new MessengerClientEvent("WATCH_STREAK", eventData));

					// Do not send watch streak on chat if no message is provided
					if (message.length > 0) {
						//Add as standard message
						const messageData: TwitchatDataTypes.MessageChatData = {
							channel_id: channelId,
							id: Utils.getUUID(),
							type: TwitchatDataTypes.TwitchatMessageType.MESSAGE,
							date: Date.now(),
							platform: "twitch",
							user,
							message,
							message_chunks,
							message_html,
							message_size,
							answers: [],
							twitch_watchStreak: tags["msg-param-value"] as number,
							twitch_source: "irc",
							is_short:
								Utils.stripHTMLTags(message_html || "").length /
									(message?.length || 1) <
									0.6 || message?.length < 4,
						};
						TwitchUtils.flagSharedChatMessage(
							messageData,
							tags["source-room-id"],
							tags["room-id"] as string,
						);
						this.dispatchEvent(new MessengerClientEvent("MESSAGE", messageData));
					}
				}

				if (tags["msg-id"] === "modiversary") {
					const channelId = tags["room-id"] as string;
					const user = this.getUserFromTags(tags, channelId);

					const params = parsed.params as string[];
					const message = params[1] || "";
					const message_chunks = TwitchUtils.parseMessageToChunks(
						message,
						tags["emotes-raw"],
						!tags["emotes-raw"],
					);
					const message_html = TwitchUtils.messageChunksToHTML(message_chunks);
					const message_size = TwitchUtils.computeMessageSize(message_chunks);

					//Add modiversary specific message
					const eventData: TwitchatDataTypes.MessageModiversaryData = {
						channel_id: channelId,
						id: Utils.getUUID(),
						type: TwitchatDataTypes.TwitchatMessageType.USER_MODIVERSARY,
						date: Date.now(),
						platform: "twitch",
						months: tags["msg-param-months"] as number,
						user,
						message,
						message_chunks,
						message_html,
						message_size,
					};
					TwitchUtils.flagSharedChatMessage(
						eventData,
						tags["source-room-id"],
						tags["room-id"] as string,
					);
					this.dispatchEvent(new MessengerClientEvent("MODIVERSARY", eventData));

					//Add as standard message
					const messageData: TwitchatDataTypes.MessageChatData = {
						channel_id: channelId,
						id: Utils.getUUID(),
						type: TwitchatDataTypes.TwitchatMessageType.MESSAGE,
						date: Date.now(),
						platform: "twitch",
						user,
						message,
						message_chunks,
						message_html,
						message_size,
						answers: [],
						twitch_modiversary: eventData.months,
						is_short:
							Utils.stripHTMLTags(message_html || "").length /
								(message?.length || 1) <
								0.6 || message?.length < 4,
					};
					TwitchUtils.flagSharedChatMessage(
						messageData,
						tags["source-room-id"],
						tags["room-id"] as string,
					);
					this.dispatchEvent(new MessengerClientEvent("MESSAGE", messageData));
				}

				//Handle subgift summaries
				//Grabs the number of subgifts made on the channel and store it to the user
				if (tags["msg-param-sender-count"]) {
					// console.log("RECEIVED SUBGIFT INFO", data);
					const channelId = tags["room-id"] as string;
					const user = this.getUserFromTags(tags, channelId);
					const total = this.getNumValueFromTag(tags["msg-param-sender-count"], 1);
					// console.log("User", user);
					// console.log("Channel", channelId);
					// console.log("Total", total);
					user.channelInfo[channelId]!.totalSubgifts = total;
				}
				break;
			}

			case "RECONNECT": {
				Logger.instance.log("irc", { info: "Received RECONNECT request from Twitch" });
				break;
			}

			//Using this instead of the "notice" event from TMI.js as it's not
			//fired for many notices whereas here we get them all
			case "NOTICE": {
				let [msgid, _url, _cmd, channel, message] = (parsed.raw as string)
					.replace(/@msg-id=(.*) :(.*) (.*) (#.*) :(.*)/gi, "$1::$2::$3::$4::$5")
					.split("::");
				console.log("NOTICE::", msgid, message);
				const msgIdToNoticeId: {
					[msgId: string]: TwitchatDataTypes.TwitchatNoticeStringType;
				} = {
					subs_on: TwitchatDataTypes.TwitchatNoticeType.SUB_ONLY_ON,
					subs_off: TwitchatDataTypes.TwitchatNoticeType.SUB_ONLY_OFF,
					followers_on: TwitchatDataTypes.TwitchatNoticeType.FOLLOW_ONLY_ON,
					followers_off: TwitchatDataTypes.TwitchatNoticeType.FOLLOW_ONLY_OFF,
					emote_only_on: TwitchatDataTypes.TwitchatNoticeType.EMOTE_ONLY_ON,
					emote_only_off: TwitchatDataTypes.TwitchatNoticeType.EMOTE_ONLY_OFF,
					slow_on: TwitchatDataTypes.TwitchatNoticeType.SLOW_MODE_ON,
					slow_off: TwitchatDataTypes.TwitchatNoticeType.SLOW_MODE_OFF,
				};
				let noticeId: TwitchatDataTypes.TwitchatNoticeStringType =
					msgIdToNoticeId[msgid!] || TwitchatDataTypes.TwitchatNoticeType.GENERIC;
				if (!message) {
					if (msgid!.indexOf("bad_delete_message_error") > -1) {
						message = StoreProxy.i18n.t("error.delete_message");
						noticeId = TwitchatDataTypes.TwitchatNoticeType.ERROR;
					}
					const authFailure = [
						"login unsuccessful",
						"login authentication failed",
						"error logging in",
						"improperly formatted auth",
						"invalid nick",
					].some((v) => msgid!.toLowerCase().indexOf(v) > -1);
					if (authFailure) {
						this.dispatchEvent(new MessengerClientEvent("REFRESH_TOKEN"));
						//Login can fail many times in a row, only tell it once until we're back
						if (!this._authFailureNotified) {
							this._authFailureNotified = true;
							message = StoreProxy.i18n.t("error.irc_reconect");
							//These aren't bound to a channel, show it on ours
							channel = StoreProxy.auth.twitch.user.login;
							noticeId = TwitchatDataTypes.TwitchatNoticeType.ERROR;
						}
					}
				}
				if (message) {
					Logger.instance.log("irc", { info: message, data: parsed });
					if (channel) {
						this.notice(noticeId, channel, message);
					} else {
						Sentry.captureMessage(
							"Received a notice with missing channel ID: " + parsed.raw,
							"warning",
						);
					}
				}
				break;
			}
			default:
				break;
		}
	}

	private notice(
		id: TwitchatDataTypes.TwitchatNoticeStringType,
		channel: string,
		message: string,
	): void {
		const eventData: TwitchatDataTypes.MessageNoticeData = {
			id: Utils.getUUID(),
			type: TwitchatDataTypes.TwitchatMessageType.NOTICE,
			date: Date.now(),
			platform: "twitch",
			message: message,
			noticeId: id,
			channel_id: this.getChannelID(channel),
		};
		this.dispatchEvent(new MessengerClientEvent("NOTICE", eventData));
	}

	/**
	 * Get a number value for an IRC tag
	 * @param val
	 * @param defaultValue
	 * @returns
	 */
	private getNumValueFromTag(val: string | number, defaultValue: number): number {
		if (typeof val == "string") return parseInt(val);
		if (typeof val == "number") return val;
		return defaultValue;
	}
}
